export class BundleBuilder extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'item', 'item-template', 'empty-item-template']
  }

  checkValidity () {
    super.checkValidity()

    if (!this.drawer) {
      this.throwError(
        `A <bundle-builder-drawer> is required for bundle builder`,
      )
    }
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.onQuantityChange.bind(this),
    )
  }

  onQuantityChange (e) {
    const action =
      e.detail.value > e.detail.oldValue ? 'INCREMENT' : 'DECREMENT'

    if (this.currentSelectedProductCount > this.numberOfProducts) return

    const item = e.target.closest(this.els.item.selector)
    if (!item) return

    item.setActive()
    this.drawer.setCurrentCount(this.currentSelectedProductCount)
    this.drawer.setNeededCount(
      this.numberOfProducts - this.currentSelectedProductCount,
    )

    if (action === 'INCREMENT') {
      this.addItemToDrawer(item)
    } else if (action === 'DECREMENT') {
      this.removeItemFromDrawer(item)
    }

    this.complete = this.currentSelectedProductCount === this.numberOfProducts

    this.updateQuantityAdjusterMaxes()
    this.drawer.setLineItemProperty()
  }

  updateQuantityAdjusterMaxes () {
    this.els.item.elements.forEach(item => {
      const quantityAdjuster = item.quantityAdjuster
      const wantedMax =
        this.numberOfProducts -
        this.currentSelectedProductCount +
        quantityAdjuster.value

      quantityAdjuster.setAttribute(
        'max',
        (wantedMax > item.maxQuantity ? item.maxQuantity : wantedMax).toString(),
      )
    })
  }

  /**
   * @param {BundleBuilderItem} item
   */
  addItemToDrawer (item) {
    const template = this.els['item-template'].element
    const clone = template.content.cloneNode(true)

    clone.firstElementChild.dataset.key = item.dataset.key

    const titleEl = clone.querySelector('[data-title]')
    if (titleEl) {
      titleEl.textContent = item.dataset.title
    }

    const currentImageEl = item.querySelector('responsive-image')
    const cloneImageEl = clone.querySelector('responsive-image')
    if (currentImageEl && cloneImageEl) {
      cloneImageEl.outerHTML = currentImageEl.outerHTML
    }

    const firstOpenItem = this.drawer.firstOpenItem
    firstOpenItem.innerHTML = ''
    firstOpenItem.appendChild(clone)
  }

  /**
   * @param {BundleBuilderItem} item
   */
  removeItemFromDrawer (item) {
    const drawerItems = item.itemsInDrawer
    if (drawerItems.length <= 0) return

    const lastDrawerItem = drawerItems[drawerItems.length - 1]
    this.drawer.clearItem(lastDrawerItem)

    this.drawer.rejigItems()
  }

  get data () {
    return JSON.parse(this.els.data.element.textContent)
  }

  get currentSelectedProductCount () {
    return this.els.item.elements.reduce((acc, item) => acc + item.value, 0)
  }

  /**
   * @returns {number}
   */
  get numberOfProducts () {
    return this.data.numberOfProducts
  }

  /**
   * @returns {BundleBuilderDrawer}
   */
  get drawer () {
    return window.BAO.Utils.getElement('bundle-builder-drawer', {
      context: this,
    })
  }

  get complete () {
    return this.dataset.complete === 'true'
  }

  set complete (val) {
    const previousVal = this.complete
    this.dataset.complete = val.toString()

    const submit = this.drawer.els.submit.element
    submit.disabled = !val
    submit.classList.remove('btn-Button-light', 'btn-Button-dark')

    submit.classList.add(val ? 'btn-Button-dark' : 'btn-Button-light')

    if (val && !previousVal) {
      this.drawer.open = true
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder)
}

class BundleBuilderItem extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['data-title', 'data-key', 'data-max-quantity']
  }

  static get styles () {
    return `
      :host {
        display: flex;
        width: 100%;
        
        border-radius: 5px;
        outline: 2px solid transparent;
        transition: outline 0.3s ease;
      }
      
      :host([data-active="true"]) {
        outline: 2px solid var(--Color_Omo);
      }
    `
  }

  checkValidity () {
    super.checkValidity()

    if (!this.quantityAdjuster) {
      this.throwError(
        `A <quantity-adjuster> is required for bundle builder items`,
      )
    }
  }

  setActive () {
    this.dataset.active = this.active.toString()
    this.quantityAdjuster.classList.toggle('qty-Adjuster-dark', this.active)
  }

  decrement () {
    this.quantityAdjuster.onMinusClick()
  }

  get maxQuantity () {
    return this.dataset.maxQuantity
  }

  get itemsInDrawer () {
    return this.bundleBuilder.drawer.els.item.elements.filter(
      el => !!el.querySelector(`[data-key="${this.dataset.key}"]`),
    )
  }

  /**
   * @returns {QuantityAdjuster}
   */
  get quantityAdjuster () {
    return window.BAO.Utils.getElement('quantity-adjuster', { context: this })
  }

  get value () {
    return this.quantityAdjuster.value
  }

  get active () {
    return this.value > 0
  }

  get productId () {
    return this.dataset.key.split(':')[0]
  }

  get variantId () {
    return this.dataset.key.split(':')[this.dataset.key.split(':').length - 1]
  }

  /**
   * @returns {BundleBuilder}
   */
  get bundleBuilder () {
    return this.closest('bundle-builder')
  }

  /**
   * @returns {BundleBuilderDrawer}
   */
  get drawer () {
    return this.bundleBuilder.drawer
  }
}

if (!customElements.get('bundle-builder-item')) {
  customElements.define('bundle-builder-item', BundleBuilderItem)
}

class BundleBuilderDrawer extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      'header',
      'line-item-property',
      'item',
      'scroll-container',
      'submit',
      'trigger',
    ]
  }

  connectedCallback () {
    super.connectedCallback()

    this.setupItems()
    this.setupStyles()

    this.dataset.mounted = 'true'
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.trigger.element,
      'click',
      this.onTriggerClick.bind(this),
    )
    this.listeners.add(this, 'click', this.onCloseClick.bind(this))
    this.listeners.add(this, 'click', this.onItemRemove.bind(this))
  }

  setupItems () {
    this.els.item.elements.forEach(item => this.createEmptyCard(item))

    const currentItemWidth = this.els.item.element.offsetWidth
    const itemCount = this.els.item.elements.length
    const itemsAreSmallerThanScrollWidth =
      currentItemWidth * itemCount <
      this.els['scroll-container'].element.offsetWidth

    if (itemsAreSmallerThanScrollWidth) {
      this.els['scroll-container'].element.classList.add(
        'bb-Drawer_ScrollContainer-normalScroll',
      )
    }
  }

  setupStyles () {
    this.style.setProperty(
      '--Closed_Height',
      `${this.els.header.element.offsetHeight}px`,
    )

    document.documentElement.style.setProperty(
      '--Bundle_Drawer_Height',
      `${this.els.header.element.offsetHeight}px`,
    )
  }

  onTriggerClick () {
    this.open = !this.open
  }

  onCloseClick (e) {
    if (!e.target.closest('[data-bundle-builder-drawer-close]')) return

    this.open = false
  }

  onItemRemove (e) {
    if (!e.target.closest('[data-item-remove]')) return

    const key = e.target.closest('[data-key]').dataset.key

    const bundleItem = this.bundleBuilder.els.item.elements.find(
      item => item.dataset.key === key,
    )
    bundleItem.decrement()

    this.rejigItems()
  }

  setCurrentCount (count) {
    this.els?.['current-count']?.elements?.forEach(el => {
      el.textContent = count.toString()
    })
  }

  setNeededCount (count) {
    this.els?.['needed-count']?.elements?.forEach(el => {
      el.textContent = count.toString()
    })
  }

  createEmptyCard (item) {
    const template = this.bundleBuilder.els['empty-item-template'].element
    const clone = template.content.cloneNode(true)
    item.appendChild(clone)
  }

  clearItem (item) {
    item.innerHTML = ''
    this.createEmptyCard(item)
  }

  rejigItems () {
    const itemToMoveToEnd = this.els.item.elements.find(
      item => !!item.querySelector('[data-empty-card]'),
    )
    itemToMoveToEnd.parentElement.appendChild(itemToMoveToEnd)

    this.els.item.elements.forEach((item, index) => {
      item.dataset.index = index.toString()
    })
  }

  setLineItemProperty () {
    this.els[
      'line-item-property'
    ].element.value = this.bundleBuilder.els.item.elements
      .filter(item => item.itemsInDrawer.length > 0)
      .map(item => `${item.productId}:${item.itemsInDrawer.length}`)
      .join(',')
  }

  get firstOpenItem () {
    return this.els.item.elements.find(
      item => !!item.querySelector('[data-empty-card]'),
    )
  }

  get open () {
    return this.dataset.open === 'true'
  }

  set open (val) {
    this.dataset.open = val.toString()
  }

  /**
   * @returns {BundleBuilder}
   */
  get bundleBuilder () {
    return this.closest('bundle-builder')
  }
}

if (!customElements.get('bundle-builder-drawer')) {
  customElements.define('bundle-builder-drawer', BundleBuilderDrawer)
}
